import type { Core } from 'chessboard/core/config/types';
import { Plugins } from 'chessboard/core/plugins/config/types';
import type { Plugins as PluginsAPI } from 'utils/plugins/config/types';
import { Logger } from 'api/logger/config/types';
import { getPluginsOrderedByRequires } from 'core/plugins/model/get-plugins-ordered-by-requires';
import { createPluginsAPI } from 'utils/plugins';
import { buildErrorMsg } from './model/build-error-message';

/**
 * Creates an API to add/remove plugins on the board.
 */
export function createChessboardPluginsAPI(
  initialPlugins: Plugins.Model[] = [],
  logger?: Logger.API,
): Plugins.API {
  const pluginsAPI = createPluginsAPI<Plugins.Model, Core.Context>();

  pluginsAPI.addMany(<PluginsAPI.Model<Plugins.Model, Core.Context>[]>initialPlugins);

  return {
    add,
    addMany: add,
    get: pluginsAPI.getAll,
    has: pluginsAPI.has,
    remove,
    setCreatePluginContext: pluginsAPI.setContext,
  };

  /**
   * Load a plugin.
   */
  function loadPlugin(plugin: Plugins.Model): void {
    const context = pluginsAPI.getContext();

    if (!context) {
      return;
    }

    const { api, apiOverrides, modeOverrides, name } = plugin;

    if (api) {
      plugin.destroyAPIMethods = context.api.extendAPI(name, { [name]: api });
    }

    if (apiOverrides) {
      plugin.destroyAPIOverrides = context.api.overrideAPI(name, apiOverrides);
    }

    if (modeOverrides) {
      plugin.destroyModeOverrides = context.mode.extendMode(modeOverrides);
    }
  }

  /**
   * Add a plugin(s).
   */
  function add(newPlugins: Plugins.Model | Plugins.Model[]): void {
    const pluginsToAdd = Array.isArray(newPlugins) ? newPlugins : [newPlugins];
    const newOrderedPlugins: Plugins.Model[] = getPluginsOrderedByRequires(
      pluginsToAdd,
      pluginsAPI.getAll(),
    );

    // here we load only the new plugins passed to add() which have been ordered by dependencies
    newOrderedPlugins.forEach((plugin) => {
      if (pluginsAPI.has(plugin.name)) {
        pluginsAPI.remove(plugin.name);
      }

      loadPlugin(plugin);

      const added = pluginsAPI.add(<PluginsAPI.Model<Plugins.Model, Core.Context>>plugin);

      if (!added) {
        const { name } = plugin;

        logger?.log({
          code: Plugins.LogCodes.CreateFailure,
          data: {
            currentPlugins: pluginsAPI.getAll().map((currentPlugin) => currentPlugin.name),
            data: buildErrorMsg(newPlugins),
            plugin: name,
          },
          level: Logger.Levels.Error,
          message: `Could not create ${name} plugin`,
        });

        remove(plugin.name);
      }
    });
  }

  /**
   * Remove a plugin.
   */
  function remove(key: string): void {
    if (!pluginsAPI.has(key)) {
      return;
    }

    const plugin = pluginsAPI.getOne(key);

    pluginsAPI.remove(key);

    plugin?.destroyAPIMethods?.();
    plugin?.destroyAPIOverrides?.();
    plugin?.destroyModeOverrides?.();
  }
}
